"use client";

import { Row, Col, Form, Toast, ToastContainer } from "react-bootstrap";
import { useForm, UseFormRegisterReturn } from "react-hook-form";
import NavList from "../NavList";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useState } from "react";
import { useUser } from "../../../context/UserContext";
import GoogleSignInButton from "../../Authentication/GoogleSignInButton";

interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Campo de contraseña con el botón de ver/ocultar del login.
const PasswordField = ({
  label,
  registration,
  error,
}: {
  label: string;
  registration: UseFormRegisterReturn;
  error?: string;
}) => {
  const [show, setShow] = useState(false);
  return (
    <Form.Group className="mb-4">
      <label className="label text-secondary">{label}</label>
      <div style={{ position: "relative" }}>
        <Form.Control
          type={show ? "text" : "password"}
          className="h-55 text-dark"
          {...registration}
          isInvalid={!!error}
          // Sin el ícono de error de Bootstrap: ocuparía el lugar del ojo.
          style={{ paddingRight: "40px", backgroundImage: "none" }}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: 0,
          }}
        >
          <span className="material-symbols-outlined">
            {show ? "visibility_off" : "visibility"}
          </span>
        </button>
      </div>
      {error && <small className="text-danger">{error}</small>}
    </Form.Group>
  );
};

const ChangePasswordForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm<FormData>();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success");

  const { patchData: usePatch } = useDataFetch();
  const { mutateAsync: updatePassword } = usePatch("/users/update/password/user", {});

  const newPassword = watch("newPassword");

  // Cuenta creada con Google sin contraseña: la define sin pedir la actual.
  const { userData, refetchUserData } = useUser();
  const isSettingPassword = (userData as any)?.has_password === false;
  const isGoogleLinked = !!(userData as any)?.google_id;
  // Cuenta vinculada que tiene una contraseña que nunca definió (p. ej. la
  // asignada al crearla): confirmar con Google reemplaza la contraseña actual.
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const skipCurrentPassword = isSettingPassword || !!googleCredential;

  const onSubmit = async (data: FormData) => {
    try {
      const response = await updatePassword({
        password: skipCurrentPassword ? undefined : data.currentPassword,
        newPassword: data.newPassword,
        google_credential: googleCredential ?? undefined,
      });

      if (response?.message === "wrong_passwords") {
        setToastMessage("La contraseña actual no es correcta.");
        setToastVariant("danger");
      } else if (response?.message === "google_verification_failed") {
        setToastMessage("No pudimos confirmar tu cuenta. Usa la misma cuenta de Google vinculada.");
        setToastVariant("danger");
        setGoogleCredential(null);
      } else {
        setToastMessage(
          skipCurrentPassword
            ? "Contraseña creada. Ya puedes ingresar con tu correo y contraseña."
            : "Contraseña actualizada correctamente"
        );
        setToastVariant("success");
        reset();
        setGoogleCredential(null);
        if (isSettingPassword) refetchUserData();
      }
    } catch (error: any) {
      setToastMessage("Error al cambiar la contraseña");
      setToastVariant("danger");
    }

    setShowToast(true);
  };

  return (
    <>
      <div className="bg-white p-4 rounded-3 mb-4">
        <NavList />

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {isGoogleLinked && (
              <Col lg={12}>
                <div className="d-flex align-items-start gap-2 border rounded-3 p-3 mb-4">
                  <img src="/images/google.svg" alt="Google" width={20} height={20} />
                  <div>
                    <div className="fw-semibold">Cuenta vinculada con Google</div>
                    <small className="text-secondary">
                      {isSettingPassword
                        ? "Aún no tienes contraseña. Define una para ingresar también con tu correo electrónico."
                        : "Puedes ingresar con Google o con tu correo electrónico y contraseña."}
                    </small>
                    {!isSettingPassword && !googleCredential && (
                      <div className="mt-2">
                        <small className="d-block mb-2">
                          ¿Nunca creaste una contraseña? Confirma con Google y crea una
                          nueva sin la actual:
                        </small>
                        <div style={{ maxWidth: 320 }}>
                          <GoogleSignInButton onCredential={setGoogleCredential} />
                        </div>
                      </div>
                    )}
                    {googleCredential && (
                      <small className="d-block mt-2 text-success fw-medium">
                        Identidad confirmada con Google. Crea tu nueva contraseña.
                      </small>
                    )}
                  </div>
                </div>
              </Col>
            )}

            {!skipCurrentPassword && (
              <Col lg={6}>
                <PasswordField
                  label="Contraseña actual"
                  registration={register("currentPassword", { required: "Campo obligatorio" })}
                  error={errors.currentPassword?.message}
                />
              </Col>
            )}

            <Col lg={6}>
              <PasswordField
                label="Nueva contraseña"
                registration={register("newPassword", {
                  required: "Campo obligatorio",
                  minLength: {
                    value: 10,
                    message: "Debe tener al menos 10 caracteres"
                  }
                })}
                error={errors.newPassword?.message}
              />
            </Col>

            {/* Sin contraseña actual quedan dos campos: van en la misma fila. */}
            <Col lg={skipCurrentPassword ? 6 : 12}>
              <PasswordField
                label="Confirmar contraseña"
                registration={register("confirmPassword", {
                  required: "Campo obligatorio",
                  validate: (value) =>
                    value === newPassword || "Las contraseñas no coinciden"
                })}
                error={errors.confirmPassword?.message}
              />
            </Col>

            <Col lg={12}>
              <Form.Group className="form-group d-flex gap-3 align-items-center">
                <button
                  type="submit"
                  className="btn btn-primary py-2 px-4 fw-medium fs-16"
                >
                  <i className="ri-check-line text-white fw-medium"></i>{" "}
                  {isSettingPassword ? "Establecer contraseña" : googleCredential ? "Crear contraseña" : "Cambiar contraseña"}
                </button>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

    </>
  );
};

export default ChangePasswordForm;
